import React, { useState, useEffect, useMemo, useCallback } from "react";
import { 
  Award, TrendingUp, AlertTriangle, Trash2, Plus, 
  Calendar, DollarSign, CheckCircle2, ChevronRight, Info, User, HelpCircle, Loader2
} from "lucide-react";
import { pb } from "../services/pocketbase";
import { pontuacaoService, getColabRules } from "../services/pontuacaoService";
import { colaboradoresService } from "../services/colaboradoresService";

const parseCurrency = (value) => {
  if (!value) return 0;
  try {
    if (typeof value === "number") return value;
    let str = String(value).trim().replace("R$", "").trim();
    if (str.includes(",")) str = str.replace(/\./g, "").replace(",", ".");
    const parsed = parseFloat(str);
    return isNaN(parsed) ? 0 : parsed;
  } catch (e) {
    return 0;
  }
};

const cleanAdmissionDate = (dateStr) => {
  if (!dateStr) return "";
  const match = String(dateStr).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : String(dateStr).substring(0, 10);
};

const formatAdmissao = (dateStr) => {
  if (!dateStr) return "";
  try {
    const cleanDateStr = cleanAdmissionDate(dateStr);
    const parts = cleanDateStr.split("-");
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  } catch (e) {
    return dateStr;
  }
};

const isColaboradorActiveInMonth = (dataAdmissao, mes, ano) => {
  if (!dataAdmissao) return true;
  try {
    const cleanDate = cleanAdmissionDate(dataAdmissao);
    const admissionDate = new Date(cleanDate + "T00:00:00");
    const firstDayOfMonth = new Date(ano, mes - 1, 1);
    return admissionDate <= firstDayOfMonth;
  } catch (e) {
    return true;
  }
};

const getColaboradoresArray = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch (err) {}
    return raw.split(",").map(s => s.trim()).filter(Boolean);
  }
  return [];
};

const calculateWorkedDaysForMonth = (colabId, userConfig, userEscalas, monthPontoRegistros, mes, ano) => {
  const totalDays = new Date(ano, mes, 0).getDate();
  const userRecords = monthPontoRegistros.filter(r => r.colaborador === colabId);
  
  const admissionDateStr = userConfig?.data_admissao ? userConfig.data_admissao.substring(0, 10) : null;
  const dismissalDateStr = userConfig?.data_desligamento ? userConfig.data_desligamento.substring(0, 10) : null;

  let totalUteis = 0;
  let faltas = 0;
  let atestados = 0;
  let ferias = 0;

  const pad = (n) => String(n).padStart(2, "0");
  const todayStr = new Date().toISOString().substring(0, 10);

  for (let day = 1; day <= totalDays; day++) {
    const dateStr = `${ano}-${pad(mes)}-${pad(day)}`;
    
    // Neutral day check
    const isBeforeAdmission = admissionDateStr && dateStr < admissionDateStr;
    const isAfterDismissal = dismissalDateStr && dateStr > dismissalDateStr;
    if (isBeforeAdmission || isAfterDismissal) continue;

    // Expected work check
    const scale = userEscalas.find(e => e.data === dateStr);
    let expectedMin = 0;
    if (scale) {
      const entrada = scale.jornada_entrada || "";
      const saida = scale.jornada_saida || "";
      if (entrada && saida) {
        const timeToMinutes = (t) => {
          if (!t) return 0;
          const [h, m] = t.split(":").map(Number);
          return h * 60 + m;
        };
        expectedMin = timeToMinutes(saida) - timeToMinutes(entrada);
      }
    } else {
      const dateObj = new Date(dateStr + "T00:00:00");
      const dayOfWeek = dateObj.getDay();
      const days = ["dom", "seg", "ter", "qua", "qui", "sex", "sab"];
      const dayKey = `jornada_${days[dayOfWeek]}`;
      
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        if (userConfig && userConfig[dayKey]) {
          expectedMin = userConfig[dayKey].horas_esperadas || 480;
        } else {
          expectedMin = 480;
        }
      }
    }

    if (expectedMin <= 0) continue;

    totalUteis++;

    const savedRecord = userRecords.find(r => r.data === dateStr);
    const isPast = dateStr < todayStr;

    if (savedRecord) {
      const batidas = (savedRecord.batidas_ajustadas && savedRecord.batidas_ajustadas.length > 0)
        ? savedRecord.batidas_ajustadas
        : (savedRecord.batidas_originais || []);

      if (batidas.length === 0) {
        const just = String(savedRecord.justificativa_abono || "").toLowerCase();
        const hasCert = !!savedRecord.certificado_medico;

        if (hasCert || just.includes("atestado")) {
          atestados++;
        } else if ((just.includes("feria") || just.includes("férias")) && !just.includes("feriado")) {
          ferias++;
        } else {
          const abono = savedRecord.abono_minutos || 0;
          if (abono < expectedMin) {
            faltas++;
          }
        }
      }
    } else if (isPast) {
      faltas++;
    }
  }

  const workedDays = Math.max(0, totalUteis - faltas - atestados - ferias);

  return {
    totalUteis,
    workedDays,
    parsed: {
      faltas,
      atestados,
      ferias
    }
  };
};

const getActiveMonthsInYear = (dataAdmissao, ano) => {
  const activeMonths = [];
  for (let m = 1; m <= 12; m++) {
    if (isColaboradorActiveInMonth(dataAdmissao, m, ano)) {
      activeMonths.push(m);
    }
  }
  return activeMonths;
};

const computeFaturamentoForMonth = (prodRecords, mes, ano) => {
  const ordersMap = new Map();

  prodRecords.forEach(r => {
    const createdDate = new Date(r.created);
    const convertedDate = r.full_data?.convertedAt ? new Date(r.full_data.convertedAt) : null;
    
    const matchesCreated = createdDate.getFullYear() === Number(ano) && (createdDate.getMonth() + 1) === Number(mes);
    const matchesConverted = convertedDate && convertedDate.getFullYear() === Number(ano) && (convertedDate.getMonth() + 1) === Number(mes);

    if (matchesCreated || matchesConverted) {
      const orderNum = r.order_number || r.full_data?.order?.number || r.full_data?.orderNumber || "S/N";
      if (!ordersMap.has(orderNum)) {
        ordersMap.set(orderNum, {
          paymentTotal: parseCurrency(r.full_data?.order?.payment?.total || r.full_data?.total_value || r.total_value || 0),
          shippingPrice: parseCurrency(r.full_data?.order?.shipping?.price || r.full_data?.shipping?.price || 0),
          items: []
        });
      }

      const isCancelled = r.status === "CANCELADO" || r.full_data?.logisticsStatus === "CANCELADO";
      const isGolpe = r.status === "GOLPE" || r.full_data?.logisticsStatus === "GOLPE";

      ordersMap.get(orderNum).items.push({
        fromStock: !!r.full_data?.fromStock,
        isCancelled,
        isGolpe,
        convertedAtStr: r.full_data?.convertedAt || r.created
      });
    }
  });

  let total = 0;

  ordersMap.forEach((info) => {
    const netOrderTotal = Math.max(0, info.paymentTotal - info.shippingPrice);
    
    const targetMonthFactoryItems = info.items.filter(item => {
      if (item.fromStock || item.isCancelled || item.isGolpe) return false;
      
      if (item.convertedAtStr) {
        const d = new Date(item.convertedAtStr);
        return (d.getMonth() + 1 === Number(mes) && d.getFullYear() === Number(ano));
      }
      return false;
    });

    if (targetMonthFactoryItems.length > 0) {
      const totalItemsCount = info.items.length || 1;
      const proportionalVal = (netOrderTotal / totalItemsCount) * targetMonthFactoryItems.length;
      total += proportionalVal;
    }
  });

  return total;
};

const getDefaultConfigs = (usersList) => {
  const leaderNames = ["CAMILLE", "RICARDO", "CACO"];
  const excludedNames = ["N8N", "OFICINA", "TEST DIAGNOSTICO", "DANIEL BARBOSA", "ALESSANDRA", "JOSÉ ALBERTO"];

  const configs = [];
  const leaders = usersList.filter(u => leaderNames.includes(u.name.trim().toUpperCase()));
  
  usersList.forEach(u => {
    const normName = u.name.trim().toUpperCase();
    if (excludedNames.includes(normName)) return;

    const isLider = leaderNames.includes(normName);
    let liderId = null;
    
    if (!isLider) {
      const defaultLeader = leaders.find(l => l.name.trim().toUpperCase() === "CACO") || leaders[0];
      liderId = defaultLeader ? defaultLeader.id : null;
    }

    configs.push({
      id: "default_" + u.id,
      colaborador: u.id,
      lider: liderId,
      is_lider: isLider,
      bonus_base: normName === "CACO" ? 300 : (normName === "CAMILLE" ? 200 : 300),
      expand: {
        colaborador: u,
        lider: liderId ? leaders.find(l => l.id === liderId) : null
      }
    });
  });

  return configs;
};

export default function PontuacaoTab({ user }) {
  const isMaster = user?.role === "master";

  const [activeSubtab, setActiveSubtab] = useState("dashboard"); // "dashboard" | "lancar" | "gerenciar" | "equipes"
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [viewMode, setViewMode] = useState("mensal"); // "mensal" | "anual"
  const [selectedAnnualColabId, setSelectedAnnualColabId] = useState("");
  const [editDataAdmissao, setEditDataAdmissao] = useState("");

  // Form states (Lançar ocorrência)
  const [formColabId, setFormColabId] = useState("");
  const [formDate, setFormDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [formDescription, setFormDescription] = useState("");
  const [formPoints, setFormPoints] = useState(1);
  const [submittingOccurrence, setSubmittingOccurrence] = useState(false);

  // Search/Filter for occurrences management
  const [searchQuery, setSearchQuery] = useState("");

  // Detail Modal (Admin looking at collaborator occurrences)
  const [detailColab, setDetailColab] = useState(null);

  // Configuration states
  const [colabConfigs, setColabConfigs] = useState([]);
  const [editingColab, setEditingColab] = useState(null);
  const [editIsLider, setEditIsLider] = useState(false);
  const [editLider, setEditLider] = useState("");
  const [editBonusBase, setEditBonusBase] = useState(0);

  // Months lists
  const MONTHS = [
    { value: 0, label: "Ano Completo" },
    { value: 1, label: "Janeiro" },
    { value: 2, label: "Fevereiro" },
    { value: 3, label: "Março" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Maio" },
    { value: 6, label: "Junho" },
    { value: 7, label: "Julho" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Setembro" },
    { value: 10, label: "Outubro" },
    { value: 11, label: "Novembro" },
    { value: 12, label: "Dezembro" }
  ];

  const yearsList = useMemo(() => {
    const current = new Date().getFullYear();
    return [current - 1, current, current + 1];
  }, []);

  // Caching states (Year-based)
  const [yearlyOccurrences, setYearlyOccurrences] = useState([]);
  const [yearlyGoals, setYearlyGoals] = useState([]);
  const [yearlyPayments, setYearlyPayments] = useState([]);
  const [yearlyOrders, setYearlyOrders] = useState([]);

  // Data states
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingGoal, setSavingGoal] = useState(false);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [scales, setScales] = useState([]);

  // Detailed loading steps
  const [loadingSteps, setLoadingSteps] = useState([
    { id: "users", label: "Carregando usuários e colaboradores...", status: "pending" },
    { id: "configs", label: "Carregando configurações de equipes...", status: "pending" },
    { id: "occurrences", label: "Buscando histórico de ocorrências do ano...", status: "pending" },
    { id: "goals", label: "Buscando metas financeiras...", status: "pending" },
    { id: "payments", label: "Carregando controle de pagamentos...", status: "pending" },
    { id: "orders", label: "Buscando ordens de produção de fábrica...", status: "pending" }
  ]);

  // --- DERIVED MEMO STATES (Cache Filtered) ---
  const occurrences = useMemo(() => {
    const targetMonth = (isMaster && activeSubtab === "anual") || (!isMaster && viewMode === "anual") ? 0 : selectedMonth;
    if (targetMonth === 0) {
      return yearlyOccurrences;
    }
    return yearlyOccurrences.filter(o => o.mes === targetMonth);
  }, [yearlyOccurrences, selectedMonth, activeSubtab, viewMode, isMaster]);

  const goalRecord = useMemo(() => {
    const targetMonth = (isMaster && activeSubtab === "anual") || (!isMaster && viewMode === "anual") ? 0 : selectedMonth;
    return yearlyGoals.find(g => g.mes === targetMonth && g.ano === selectedYear) || null;
  }, [yearlyGoals, selectedMonth, selectedYear, activeSubtab, viewMode, isMaster]);

  const payments = useMemo(() => {
    return yearlyPayments.filter(p => p.mes === selectedMonth);
  }, [yearlyPayments, selectedMonth]);

  const actualSales = useMemo(() => {
    const targetMonth = (isMaster && activeSubtab === "anual") || (!isMaster && viewMode === "anual") ? 0 : selectedMonth;
    if (targetMonth === 0) {
      let sum = 0;
      for (let m = 1; m <= 12; m++) {
        sum += computeFaturamentoForMonth(yearlyOrders, m, selectedYear);
      }
      return sum;
    }
    return computeFaturamentoForMonth(yearlyOrders, selectedMonth, selectedYear);
  }, [yearlyOrders, selectedMonth, selectedYear, activeSubtab, viewMode, isMaster]);

  // Sync goal input value with goal record
  const [goalInput, setGoalInput] = useState("");
  useEffect(() => {
    setGoalInput(goalRecord ? String(goalRecord.meta) : "");
  }, [goalRecord]);

  // --- FETCH DATA ---
  const loadData = useCallback(async (targetYear) => {
    setLoading(true);
    
    const updateStep = (id, status) => {
      setLoadingSteps(prev => prev.map(s => s.id === id ? { ...s, status } : s));
    };

    // Reset steps
    setLoadingSteps(prev => prev.map(s => ({ ...s, status: "pending" })));

    try {
      // Step 1: Users
      updateStep("users", "loading");
      const usersList = await pb.collection("users").getFullList({
        filter: 'role != "motoboy"',
        sort: "name"
      });
      setCollaborators(usersList);
      updateStep("users", "success");

      // Auto-Sync Local Configs from yesterday
      if (!window.isSyncingColaboradores) {
        const localDataStr = localStorage.getItem("pontuacao_colaboradores_local");
        if (localDataStr) {
          window.isSyncingColaboradores = true;
          try {
            const localList = JSON.parse(localDataStr);
            if (Array.isArray(localList) && localList.length > 0) {
              const remainingLocal = [];
              let syncedCount = 0;
              for (const localItem of localList) {
                try {
                  const res = await pontuacaoService.saveColaboradorConfig(localItem.colaborador, {
                    is_lider: localItem.is_lider,
                    lider: localItem.lider,
                    bonus_base: localItem.bonus_base,
                    data_admissao: localItem.data_admissao,
                    colaboradorName: localItem.expand?.colaborador?.name,
                    liderName: localItem.expand?.lider?.name
                  });
                  
                  if (res && !res.isLocal) {
                    syncedCount++;
                  } else {
                    remainingLocal.push(localItem);
                  }
                } catch (syncErr) {
                  console.error("Failed to sync item:", localItem, syncErr);
                  remainingLocal.push(localItem);
                }
              }
              if (syncedCount > 0) {
                alert(`Sincronização realizada: ${syncedCount} configurações de colaboradores que estavam salvas localmente foram salvas no banco de dados com sucesso!`);
                if (remainingLocal.length > 0) {
                  localStorage.setItem("pontuacao_colaboradores_local", JSON.stringify(remainingLocal));
                } else {
                  localStorage.removeItem("pontuacao_colaboradores_local");
                }
              }
            }
          } catch (e) {
            console.error("Error during auto-sync:", e);
          } finally {
            window.isSyncingColaboradores = false;
          }
        }
      }

      // Step 2: Configs
      updateStep("configs", "loading");
      const configs = await pontuacaoService.fetchColaboradoresConfig();
      
      // De-duplicate configs by colaborador to prevent UI duplicates
      const uniqueConfigs = [];
      const seenColabs = new Set();
      for (const config of configs) {
        if (config.colaborador && !seenColabs.has(config.colaborador)) {
          seenColabs.add(config.colaborador);
          uniqueConfigs.push(config);
        }
      }

      let finalConfigs = uniqueConfigs;
      if (uniqueConfigs.length === 0) {
        finalConfigs = getDefaultConfigs(usersList);
      }
      setColabConfigs(finalConfigs);
      updateStep("configs", "success");

      // Step 3: Occurrences
      updateStep("occurrences", "loading");
      const occList = await pb.collection("pontuacao_ocorrencias").getFullList({
        filter: `ano = ${Number(targetYear)}`,
        expand: "colaborador,responsavel",
        sort: "-data"
      });
      setYearlyOccurrences(occList);
      updateStep("occurrences", "success");

      // Step 4: Goals
      updateStep("goals", "loading");
      const goalsList = await pontuacaoService.fetchYearlyGoals(targetYear);
      setYearlyGoals(goalsList);
      updateStep("goals", "success");

      // Step 5: Payments
      updateStep("payments", "loading");
      const paymentsList = await pontuacaoService.fetchYearlyPayments(targetYear);
      setYearlyPayments(paymentsList);
      updateStep("payments", "success");

      // Step 6: Production orders
      updateStep("orders", "loading");
      const ordersList = await pontuacaoService.fetchYearlyProductionOrders(targetYear);
      setYearlyOrders(ordersList);
      updateStep("orders", "success");

      // Saturday scales
      try {
        const scalesList = await colaboradoresService.fetchEscalas();
        setScales(scalesList);
      } catch (err) {
        console.warn("Error fetching scales in PontuacaoTab:", err);
      }

      // Attendance records for target year
      try {
        const startStr = `${targetYear}-01-01`;
        const endStr = `${targetYear}-12-31`;
        const listPontos = await pb.collection("colaboradores_ponto_registros").getFullList({
          filter: `data >= "${startStr}" && data <= "${endStr}"`,
          sort: "data"
        });
        setAttendanceRecords(listPontos);
      } catch (err) {
        console.warn("Error fetching attendance records in PontuacaoTab:", err);
      }

    } catch (err) {
      console.error("Error loading points data:", err);
      setLoadingSteps(prev => prev.map(s => s.status === "pending" || s.status === "loading" ? { ...s, status: "error" } : s));
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 300);
    }
  }, []);

  const refreshOccurrences = async () => {
    try {
      const occList = await pb.collection("pontuacao_ocorrencias").getFullList({
        filter: `ano = ${Number(selectedYear)}`,
        expand: "colaborador,responsavel",
        sort: "-data"
      });
      setYearlyOccurrences(occList);
    } catch (e) {
      console.error("Failed to refresh occurrences in background:", e);
    }
  };

  useEffect(() => {
    loadData(selectedYear);
  }, [selectedYear, loadData]);

  // --- ACTION HANDLERS ---
  const handleSaveGoal = async (e) => {
    if (e) e.preventDefault();
    const val = parseFloat(goalInput);
    if (isNaN(val) || val <= 0) return alert("Meta inválida.");
    setSavingGoal(true);
    try {
      const targetMonth = (isMaster && activeSubtab === "anual") ? 0 : selectedMonth;
      const updated = await pontuacaoService.saveMonthlyGoal(targetMonth, selectedYear, val);
      setYearlyGoals(prev => {
        const idx = prev.findIndex(g => g.mes === updated.mes && g.ano === updated.ano);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = updated;
          return next;
        } else {
          return [...prev, updated];
        }
      });
      alert("Meta salva com sucesso!");
    } catch (err) {
      alert("Erro ao salvar meta: " + err.message);
    } finally {
      setSavingGoal(false);
    }
  };

  const handleLaunchOccurrence = async (e) => {
    e.preventDefault();
    if (!formColabId) return alert("Selecione o colaborador.");
    if (!formDescription.trim()) return alert("Insira uma descrição para a ocorrência.");
    
    setSubmittingOccurrence(true);
    try {
      const d = new Date(formDate + "T12:00:00");
      const occMonth = d.getMonth() + 1;
      const occYear = d.getFullYear();

      if (formColabId.startsWith("team:")) {
        const leaderId = formColabId.replace("team:", "");
        const teamMembers = colabConfigs.filter(c => c.lider === leaderId);
        const targets = [leaderId, ...teamMembers.map(m => m.colaborador)];
        
        for (const targetId of targets) {
          await pontuacaoService.createOccurrence({
            colaborador: targetId,
            data: formDate,
            mes: occMonth,
            ano: occYear,
            ocorrencia: `[Equipe] ${formDescription.trim()}`,
            pontos_debitados: Number(formPoints),
            responsavel: user?.id || ""
          });
        }
        alert("Ocorrência lançada para toda a equipe com sucesso!");
      } else {
        await pontuacaoService.createOccurrence({
          colaborador: formColabId,
          data: formDate,
          mes: occMonth,
          ano: occYear,
          ocorrencia: formDescription.trim(),
          pontos_debitados: Number(formPoints),
          responsavel: user?.id || ""
        });

        // Duplicação automática para o líder da equipe
        const colabConfig = colabConfigs.find(c => c.colaborador === formColabId);
        if (colabConfig && colabConfig.lider) {
          const memberUser = collaborators.find(u => u.id === formColabId);
          const memberName = memberUser ? memberUser.name.split(" ")[0] : "Membro";
          
          await pontuacaoService.createOccurrence({
            colaborador: colabConfig.lider,
            data: formDate,
            mes: occMonth,
            ano: occYear,
            ocorrencia: `[Líder - ${memberName}] ${formDescription.trim()}`,
            pontos_debitados: Number(formPoints),
            responsavel: user?.id || ""
          });
        }
        alert("Ocorrência lançada com sucesso!");
      }

      setFormDescription("");
      setFormPoints(1);
      await refreshOccurrences();
    } catch (err) {
      alert("Erro ao lançar ocorrência: " + err.message);
    } finally {
      setSubmittingOccurrence(false);
    }
  };

  const handleDeleteOccurrence = async (id) => {
    if (!window.confirm("Deseja realmente excluir esta ocorrência?")) return;
    try {
      await pontuacaoService.deleteOccurrence(id);
      setYearlyOccurrences(prev => prev.filter(o => o.id !== id));
      alert("Ocorrência excluída!");
    } catch (err) {
      alert("Erro ao excluir ocorrência: " + err.message);
    }
  };

  const handleTogglePayment = async (colaboradorId, valor, newStatus) => {
    if (newStatus === "pago" && companyGoalPercentage < 100) {
      const confirmOverride = window.confirm("Atenção: A meta global deste mês não foi atingida. Deseja realizar o pagamento do bônus mesmo assim?");
      if (!confirmOverride) return;
    } else if (newStatus === "pago") {
      const confirmPay = window.confirm(`Deseja marcar o bônus de R$ ${valor.toFixed(2)} como pago?`);
      if (!confirmPay) return;
    } else if (newStatus === "pendente") {
      const confirmUndo = window.confirm("Deseja desfazer o pagamento deste bônus?");
      if (!confirmUndo) return;
    }

    try {
      const updated = await pontuacaoService.savePayment(colaboradorId, selectedMonth, selectedYear, valor, newStatus);
      setYearlyPayments(prev => {
        const idx = prev.findIndex(p => p.colaborador === colaboradorId && p.mes === selectedMonth && p.ano === selectedYear);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = updated;
          return next;
        } else {
          return [...prev, updated];
        }
      });
    } catch (err) {
      alert("Erro ao salvar pagamento: " + err.message);
    }
  };

  // --- STATS COMPUTATION ---
  const companyGoalPercentage = useMemo(() => {
    const meta = goalRecord?.meta || 0;
    if (meta <= 0) return 0;
    return Math.min(100, Math.round((actualSales / meta) * 10000) / 100);
  }, [goalRecord, actualSales]);

  // Aggregated data for all collaborators (Master View)
  const collaboratorsSummary = useMemo(() => {
    if (!isMaster) return [];
    
    const targetMonth = activeSubtab === "anual" ? 0 : selectedMonth;

    return colabConfigs.map(config => {
      const colabId = config.colaborador;
      const colab = collaborators.find(u => u.id === colabId);
      if (!colab) return null;

      const maxBonus = config.bonus_base;
      const isAnnual = targetMonth === 0;

      // Determine active months
      const activeMonths = isAnnual 
        ? getActiveMonthsInYear(config.data_admissao, selectedYear)
        : isColaboradorActiveInMonth(config.data_admissao, selectedMonth, selectedYear) ? [selectedMonth] : [];

      const isActive = activeMonths.length > 0;
      const activeMonthsCount = activeMonths.length;

      const baseMaxPoints = config.is_lider ? (10 + colabConfigs.filter(c => c.lider === colabId).length * 10) : 10;
      const scaledMaxPoints = baseMaxPoints * activeMonthsCount;
      const maxBonusPeriod = maxBonus * activeMonthsCount;

      let colabOccs = [];
      let pointsLost = 0;
      let expectedBonus = 0;
      let totalFaltas = 0;
      let totalAtestados = 0;
      let totalFerias = 0;
      let totalUteis = 0;
      let totalTrabalhados = 0;

      const colabEscalas = scales.filter(e => getColaboradoresArray(e.colaboradores).includes(colabId));

      if (isActive) {
        colabOccs = occurrences.filter(o => {
          return o.colaborador === colabId && activeMonths.map(Number).includes(Number(o.mes)) && Number(o.ano) === Number(selectedYear);
        });
        
        pointsLost = colabOccs.reduce((sum, o) => sum + (o.pontos_debitados || 0), 0);

        if (isAnnual) {
          activeMonths.forEach(m => {
            const mOccs = colabOccs.filter(o => Number(o.mes) === Number(m));
            const mPointsLost = mOccs.reduce((sum, o) => sum + (o.pontos_debitados || 0), 0);
            const mRemaining = Math.max(0, baseMaxPoints - mPointsLost);
            const mPct = baseMaxPoints > 0 ? (mRemaining / baseMaxPoints) : 0;
            const mBaseBonus = maxBonus * mPct;
            
            if (config.is_lider) {
              expectedBonus += mBaseBonus;
            } else {
              const pad = (n) => String(n).padStart(2, "0");
              const monthStr = `${selectedYear}-${pad(m)}`;
              const mPontoRecords = attendanceRecords.filter(r => r.data.startsWith(monthStr));
              
              const mCalcs = calculateWorkedDaysForMonth(colabId, config, colabEscalas, mPontoRecords, m, selectedYear);
              
              expectedBonus += mBaseBonus * (mCalcs.totalUteis > 0 ? (mCalcs.workedDays / mCalcs.totalUteis) : 1);
              totalFaltas += mCalcs.parsed.faltas;
              totalAtestados += mCalcs.parsed.atestados;
              totalFerias += mCalcs.parsed.ferias;
              totalUteis += mCalcs.totalUteis;
              totalTrabalhados += mCalcs.workedDays;
            }
          });
        } else {
          const m = selectedMonth;
          const mRemaining = Math.max(0, baseMaxPoints - pointsLost);
          const mPct = baseMaxPoints > 0 ? (mRemaining / baseMaxPoints) : 0;
          const mBaseBonus = maxBonus * mPct;
          
          if (config.is_lider) {
            expectedBonus = mBaseBonus;
          } else {
            const pad = (n) => String(n).padStart(2, "0");
            const monthStr = `${selectedYear}-${pad(m)}`;
            const mPontoRecords = attendanceRecords.filter(r => r.data.startsWith(monthStr));
            
            const mCalcs = calculateWorkedDaysForMonth(colabId, config, colabEscalas, mPontoRecords, m, selectedYear);
            
            expectedBonus = mBaseBonus * (mCalcs.totalUteis > 0 ? (mCalcs.workedDays / mCalcs.totalUteis) : 1);
            totalFaltas = mCalcs.parsed.faltas;
            totalAtestados = mCalcs.parsed.atestados;
            totalFerias = mCalcs.parsed.ferias;
            totalUteis = mCalcs.totalUteis;
            totalTrabalhados = mCalcs.workedDays;
          }
        }
      }

      const remainingPoints = Math.max(0, scaledMaxPoints - pointsLost);
      const activeBonusPct = scaledMaxPoints > 0 ? (remainingPoints / scaledMaxPoints) : 0;

      return {
        id: colabId,
        name: colab.name,
        username: colab.username,
        role: colab.role,
        avatar: colab.avatar,
        maxPoints: scaledMaxPoints,
        pointsLost,
        remainingPoints,
        activeBonusPct: Math.round(activeBonusPct * 1000) / 10,
        expectedBonus,
        isLider: config.is_lider,
        occurrences: colabOccs,
        isActive,
        dataAdmissao: config.data_admissao,
        maxBonus,
        maxBonusPeriod,
        activeMonthsCount,
        totalUteis,
        totalTrabalhados,
        totalFaltas,
        totalAtestados,
        totalFerias
      };
    }).filter(Boolean);
  }, [collaborators, colabConfigs, occurrences, selectedMonth, selectedYear, activeSubtab, isMaster, scales, attendanceRecords]);

  // Personal statistics (Regular User View)
  const personalStats = useMemo(() => {
    if (isMaster) return null;
    
    const config = colabConfigs.find(c => c.colaborador === user?.id);
    if (!config) return null;

    const isAnnual = viewMode === "anual";
    const activeMonths = isAnnual 
      ? getActiveMonthsInYear(config.data_admissao, selectedYear)
      : isColaboradorActiveInMonth(config.data_admissao, selectedMonth, selectedYear) ? [selectedMonth] : [];

    const isActive = activeMonths.length > 0;
    const activeMonthsCount = activeMonths.length;

    const maxBonus = config.bonus_base;
    const baseMaxPoints = config.is_lider ? (10 + colabConfigs.filter(c => c.lider === user?.id).length * 10) : 10;
    const scaledMaxPoints = baseMaxPoints * activeMonthsCount;
    const maxBonusPeriod = maxBonus * activeMonthsCount;

    let userOccs = [];
    let pointsLost = 0;

    if (isActive) {
      const teamMembers = colabConfigs.filter(c => c.lider === user?.id);
      const teamUserIds = teamMembers.map(m => m.colaborador);
      
      userOccs = occurrences.filter(o => {
        return o.colaborador === user?.id && activeMonths.map(Number).includes(Number(o.mes)) && Number(o.ano) === Number(selectedYear);
      });
      
      pointsLost = userOccs.reduce((sum, o) => sum + (o.pontos_debitados || 0), 0);
    }

    let expectedBonus = 0;
    let totalUteis = 0;
    let totalTrabalhados = 0;
    let totalFaltas = 0;
    let totalAtestados = 0;
    let totalFerias = 0;

    const userEscalas = scales.filter(e => getColaboradoresArray(e.colaboradores).includes(user?.id));

    if (isActive) {
      if (isAnnual) {
        activeMonths.forEach(m => {
          const mOccs = userOccs.filter(o => Number(o.mes) === Number(m));
          const mPointsLost = mOccs.reduce((sum, o) => sum + (o.pontos_debitados || 0), 0);
          const mRemaining = Math.max(0, baseMaxPoints - mPointsLost);
          const mPct = baseMaxPoints > 0 ? (mRemaining / baseMaxPoints) : 0;
          const mBaseBonus = maxBonus * mPct;
          
          if (config.is_lider) {
            expectedBonus += mBaseBonus;
          } else {
            const pad = (n) => String(n).padStart(2, "0");
            const monthStr = `${selectedYear}-${pad(m)}`;
            const mPontoRecords = attendanceRecords.filter(r => r.data.startsWith(monthStr));
            
            const mCalcs = calculateWorkedDaysForMonth(user?.id, config, userEscalas, mPontoRecords, m, selectedYear);
            
            expectedBonus += mBaseBonus * (mCalcs.totalUteis > 0 ? (mCalcs.workedDays / mCalcs.totalUteis) : 1);
            totalUteis += mCalcs.totalUteis;
            totalTrabalhados += mCalcs.workedDays;
            totalFaltas += mCalcs.parsed.faltas;
            totalAtestados += mCalcs.parsed.atestados;
            totalFerias += mCalcs.parsed.ferias;
          }
        });
      } else {
        const m = selectedMonth;
        const mPointsLost = userOccs.reduce((sum, o) => sum + (o.pontos_debitados || 0), 0);
        const mRemaining = Math.max(0, baseMaxPoints - mPointsLost);
        const mPct = baseMaxPoints > 0 ? (mRemaining / baseMaxPoints) : 0;
        const mBaseBonus = maxBonus * mPct;
        
        if (config.is_lider) {
          expectedBonus = mBaseBonus;
        } else {
          const pad = (n) => String(n).padStart(2, "0");
          const monthStr = `${selectedYear}-${pad(m)}`;
          const mPontoRecords = attendanceRecords.filter(r => r.data.startsWith(monthStr));
          
          const mCalcs = calculateWorkedDaysForMonth(user?.id, config, userEscalas, mPontoRecords, m, selectedYear);
          
          expectedBonus = mBaseBonus * (mCalcs.totalUteis > 0 ? (mCalcs.workedDays / mCalcs.totalUteis) : 1);
          totalUteis = mCalcs.totalUteis;
          totalTrabalhados = mCalcs.workedDays;
          totalFaltas = mCalcs.parsed.faltas;
          totalAtestados = mCalcs.parsed.atestados;
          totalFerias = mCalcs.parsed.ferias;
        }
      }
    }

    const remainingPoints = Math.max(0, scaledMaxPoints - pointsLost);
    const activeBonusPct = scaledMaxPoints > 0 ? (remainingPoints / scaledMaxPoints) : 0;

    return {
      maxPoints: scaledMaxPoints,
      pointsLost,
      remainingPoints,
      activeBonusPct: Math.round(activeBonusPct * 1000) / 10,
      expectedBonus,
      maxBonus,
      maxBonusPeriod,
      occurrences: userOccs,
      isActive,
      activeMonthsCount,
      dataAdmissao: config.data_admissao,
      totalUteis,
      totalTrabalhados,
      totalFaltas,
      totalAtestados,
      totalFerias,
      isLider: config.is_lider
    };
  }, [occurrences, colabConfigs, isMaster, user?.id, selectedMonth, selectedYear, viewMode, scales, attendanceRecords]);

  const personalMonthlyPayment = useMemo(() => {
    if (isMaster || !user) return null;
    return yearlyPayments.find(p => p.colaborador === user.id && p.mes === selectedMonth && p.ano === selectedYear) || null;
  }, [yearlyPayments, user?.id, selectedMonth, selectedYear, isMaster]);

  const personalYearlyMonths = useMemo(() => {
    if (isMaster || !user) return [];
    
    const config = colabConfigs.find(c => c.colaborador === user.id);
    if (!config) return [];

    const baseMaxPoints = config.is_lider ? (10 + colabConfigs.filter(c => c.lider === user.id).length * 10) : 10;
    const maxBonus = config.bonus_base;

    return Array.from({ length: 12 }, (_, i) => {
      const mes = i + 1;
      const isActive = isColaboradorActiveInMonth(config.data_admissao, mes, selectedYear);
      
      // Calculate global goal % for this month
      const monthGoalRecord = yearlyGoals.find(g => g.mes === mes && g.ano === selectedYear);
      const monthSales = computeFaturamentoForMonth(yearlyOrders, mes, selectedYear);
      const monthGoalMeta = monthGoalRecord?.meta || 0;
      const monthGoalPercentage = monthGoalMeta > 0 ? Math.min(100, Math.round((monthSales / monthGoalMeta) * 10000) / 100) : 0;

      // Calculate score for this month
      let pointsLost = 0;
      let monthOccs = [];
      if (isActive) {
        const teamMembers = colabConfigs.filter(c => c.lider === user.id);
        const teamUserIds = teamMembers.map(m => m.colaborador);
        
        monthOccs = yearlyOccurrences.filter(o => {
          return o.colaborador === user.id && o.mes === mes && o.ano === selectedYear;
        });
        
        pointsLost = monthOccs.reduce((sum, o) => sum + (o.pontos_debitados || 0), 0);
      }

      const isLider = config.is_lider;
      const remainingPoints = isActive ? Math.max(0, baseMaxPoints - pointsLost) : 0;
      const activeBonusPct = isActive && baseMaxPoints > 0 ? (remainingPoints / baseMaxPoints) : 0;
      
      const baseExpectedBonus = isActive ? maxBonus * activeBonusPct : 0;
      
      let mUteis = 0;
      let mWorked = 0;
      let mParsed = { faltas: 0, atestados: 0, ferias: 0 };
      
      if (isActive && !isLider) {
        const pad = (n) => String(n).padStart(2, "0");
        const monthStr = `${selectedYear}-${pad(mes)}`;
        const mPontoRecords = attendanceRecords.filter(r => r.data.startsWith(monthStr));
        const userEscalas = scales.filter(e => getColaboradoresArray(e.colaboradores).includes(user?.id));
        
        const mCalcs = calculateWorkedDaysForMonth(user?.id, config, userEscalas, mPontoRecords, mes, selectedYear);
        mUteis = mCalcs.totalUteis;
        mWorked = mCalcs.workedDays;
        mParsed = mCalcs.parsed;
      }

      const mProportion = (isActive && isLider) ? 1 : (mUteis > 0 ? (mWorked / mUteis) : 1);
      const expectedBonus = baseExpectedBonus * mProportion;

      // Get payment record
      const paymentRecord = yearlyPayments.find(p => p.colaborador === user.id && p.mes === mes && p.ano === selectedYear);
      const isPaid = paymentRecord?.status === "pago";
      const paymentDate = paymentRecord?.data_pagamento || null;
      const paymentValue = paymentRecord?.valor_pago || 0;

      return {
        mes,
        mesLabel: MONTHS.find(m => m.value === mes)?.label || "",
        isActive,
        goalPercentage: monthGoalPercentage,
        isGoalMet: monthGoalPercentage >= 100,
        maxPoints: isActive ? baseMaxPoints : 0,
        pointsLost,
        remainingPoints,
        activeBonusPct: Math.round(activeBonusPct * 1000) / 10,
        expectedBonus,
        isPaid,
        paymentDate,
        paymentValue,
        occurrences: monthOccs,
        totalUteis: mUteis,
        workedDays: mWorked,
        parsed: mParsed,
        isLider
      };
    });
  }, [user, isMaster, colabConfigs, selectedYear, yearlyGoals, yearlyOrders, yearlyOccurrences, yearlyPayments, MONTHS, scales, attendanceRecords]);

  const personalYearlySummary = useMemo(() => {
    if (isMaster || !user) return null;
    
    const activeMonths = personalYearlyMonths.filter(m => m.isActive);
    const totalMaxPoints = activeMonths.reduce((sum, m) => sum + m.maxPoints, 0);
    const totalPointsLost = activeMonths.reduce((sum, m) => sum + m.pointsLost, 0);
    const totalRemainingPoints = activeMonths.reduce((sum, m) => sum + m.remainingPoints, 0);
    const totalExpectedBonus = activeMonths.reduce((sum, m) => sum + m.expectedBonus, 0);
    const totalPaid = activeMonths.reduce((sum, m) => sum + (m.isPaid ? m.paymentValue : 0), 0);
    const totalPending = activeMonths.reduce((sum, m) => sum + (m.isPaid ? 0 : m.expectedBonus), 0);

    return {
      maxPoints: totalMaxPoints,
      pointsLost: totalPointsLost,
      remainingPoints: totalRemainingPoints,
      expectedBonus: totalExpectedBonus,
      totalPaid,
      totalPending,
      activeMonthsCount: activeMonths.length
    };
  }, [personalYearlyMonths, isMaster, user]);

  // Filtered occurrences in Admin tab
  const filteredOccurrences = useMemo(() => {
    if (!searchQuery.trim()) return occurrences;
    const term = searchQuery.toLowerCase().trim();
    return occurrences.filter(occ => {
      const colabName = (occ.expand?.colaborador?.name || "").toLowerCase();
      const desc = (occ.ocorrencia || "").toLowerCase();
      return colabName.includes(term) || desc.includes(term);
    });
  }, [occurrences, searchQuery]);

  const paymentsTotals = useMemo(() => {
    let totalEstimado = 0;
    let totalPago = 0;
    let totalPendente = 0;

    collaboratorsSummary.forEach(colab => {
      totalEstimado += colab.expectedBonus;
      const paymentRecord = payments.find(p => p.colaborador === colab.id && p.status === "pago");
      if (paymentRecord) {
        totalPago += paymentRecord.valor_pago;
      } else {
        totalPendente += colab.expectedBonus;
      }
    });

    return { totalEstimado, totalPago, totalPendente };
  }, [collaboratorsSummary, payments]);

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] w-full bg-slate-50 overflow-hidden font-sans">
      
      {/* ─── HEADER ─── */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 shrink-0 flex flex-col sm:flex-row gap-3 justify-between items-center shadow-sm z-20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500 text-white rounded-xl flex items-center justify-center shadow-md">
            <Award size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-lg font-extrabold text-slate-800 leading-tight">Controle de Bônus</h1>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Metas e Pontuação de Equipe</p>
          </div>
        </div>

        {/* Date Selector */}
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-slate-400" />
          {((isMaster && activeSubtab !== "anual") || (!isMaster && viewMode !== "anual")) && (
            <select 
              value={selectedMonth} 
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="p-1.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 bg-white shadow-sm outline-none focus:border-amber-500"
            >
              {MONTHS.filter(m => m.value !== 0).map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          )}
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="p-1.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 bg-white shadow-sm outline-none focus:border-amber-500"
          >
            {yearsList.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        {/* Admin Navigation Options */}
        {isMaster && (
          <div className="flex bg-slate-100 p-1 rounded-xl w-full sm:w-auto mt-2 sm:mt-0">
            <button
              onClick={() => setActiveSubtab("dashboard")}
              className={`flex-1 sm:flex-none px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                activeSubtab === "dashboard" ? "bg-white text-amber-600 shadow-sm" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50/50"
              }`}
            >
              Painel Geral
            </button>
            <button
              onClick={() => setActiveSubtab("anual")}
              className={`flex-1 sm:flex-none px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                activeSubtab === "anual" ? "bg-white text-amber-600 shadow-sm" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50/50"
              }`}
            >
              Ano Completo
            </button>
            <button
              onClick={() => setActiveSubtab("pagamentos")}
              className={`flex-1 sm:flex-none px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                activeSubtab === "pagamentos" ? "bg-white text-amber-600 shadow-sm" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50/50"
              }`}
            >
              Controle de Pagamentos
            </button>
            <button
              onClick={() => setActiveSubtab("lancar")}
              className={`flex-1 sm:flex-none px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                activeSubtab === "lancar" ? "bg-white text-amber-600 shadow-sm" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50/50"
              }`}
            >
              Lançar Ocorrência
            </button>
            <button
              onClick={() => setActiveSubtab("gerenciar")}
              className={`flex-1 sm:flex-none px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                activeSubtab === "gerenciar" ? "bg-white text-amber-600 shadow-sm" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50/50"
              }`}
            >
              Gerenciar Lançamentos ({occurrences.length})
            </button>
            <button
              onClick={() => setActiveSubtab("equipes")}
              className={`flex-1 sm:flex-none px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                activeSubtab === "equipes" ? "bg-white text-amber-600 shadow-sm" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50/50"
              }`}
            >
              Configurar Equipes
            </button>
          </div>
        )}
      </div>

      {/* ─── MAIN CONTENT CONTAINER ─── */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 custom-scrollbar text-slate-850">
        
        {loading ? (
          <div className="h-full w-full flex flex-col justify-center items-center py-12">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-lg max-w-md w-full text-left">
              <div className="flex items-center gap-3 mb-5 pb-3 border-b border-slate-100">
                <Loader2 className="animate-spin text-amber-500 shrink-0" size={20} />
                <div>
                  <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">
                    Carregando Módulo de Pontuação
                  </h3>
                  <p className="text-[10px] text-slate-400 font-medium">Buscando dados anuais da fábrica e colaboradores...</p>
                </div>
              </div>
              
              <div className="space-y-3 font-semibold text-xs text-slate-600">
                {loadingSteps.map(step => (
                  <div key={step.id} className="flex items-center justify-between">
                    <span>{step.label}</span>
                    <span className="font-mono font-bold text-[10px]">
                      {step.status === "pending" && <span className="text-slate-350">Aguardando...</span>}
                      {step.status === "loading" && <span className="text-amber-550 animate-pulse">Buscando...</span>}
                      {step.status === "success" && <span className="text-emerald-600">✓ Pronto</span>}
                      {step.status === "error" && <span className="text-red-500">✕ Falha</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-5xl mx-auto space-y-6">
            
            {/* 1. PROGRESSO DA META GLOBAL (Visible to everyone) */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-[11px] font-black uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <TrendingUp size={14} className="text-amber-500" />
                  {((isMaster && activeSubtab === "anual") || (!isMaster && viewMode === "anual")) 
                    ? "Progresso da Meta Global do Ano" 
                    : "Progresso da Meta Global do Mês"}
                </span>
                {companyGoalPercentage >= 100 ? (
                  <span className="bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full text-[9px] font-black flex items-center gap-1 animate-bounce">
                    <CheckCircle2 size={10} /> META BATIDA! BÔNUS ATIVO
                  </span>
                ) : (
                  <span className="bg-amber-100 text-amber-800 px-2.5 py-0.5 rounded-full text-[9px] font-black">
                    {companyGoalPercentage}% Atingido
                  </span>
                )}
              </div>

              {/* Progress bar container */}
              <div className="w-full bg-slate-100 rounded-full h-4 overflow-hidden border border-slate-200/50">
                <div 
                  className={`h-full transition-all duration-500 ease-out bg-gradient-to-r ${
                    companyGoalPercentage >= 100 ? "from-emerald-500 to-teal-500" : "from-amber-500 to-orange-500"
                  }`}
                  style={{ width: `${companyGoalPercentage}%` }}
                ></div>
              </div>

              <p className="text-[10px] text-slate-400 mt-2 font-medium">
                * O bônus estimado dos colaboradores só é distribuído caso a meta de faturamento da empresa seja batida no mês corrente (100% ou mais).
              </p>

              {isMaster && (activeSubtab === "dashboard" || activeSubtab === "anual") && (
                <form onSubmit={handleSaveGoal} className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black uppercase text-slate-500">
                      {activeSubtab === "anual" ? "Ajustar Meta do Ano:" : "Ajustar Meta do Mês:"}
                    </span>
                    <div className="relative flex items-center gap-1">
                      <span className="text-[10px] font-black text-slate-400">R$</span>
                      <input
                        type="number"
                        placeholder={activeSubtab === "anual" ? "Ex: 3000000" : "Ex: 150000"}
                        className="w-32 px-2 py-1 border border-slate-200 bg-slate-50 hover:bg-slate-100/30 rounded-lg font-bold text-slate-800 text-xs outline-none focus:border-amber-500 focus:bg-white transition-all shadow-inner"
                        value={goalInput}
                        onChange={(e) => setGoalInput(e.target.value)}
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={savingGoal}
                      className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-[9px] rounded-lg shadow-sm transition-all uppercase active:scale-98 flex items-center justify-center gap-1"
                    >
                      {savingGoal ? <Loader2 className="animate-spin" size={8} /> : "Salvar"}
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-4 text-xs font-semibold text-slate-600">
                    <div>
                      {activeSubtab === "anual" ? "Meta Anual:" : "Meta Estabelecida:"} <span className="font-black text-slate-800">{goalRecord ? `R$ ${goalRecord.meta.toLocaleString("pt-BR")}` : "Não Definida"}</span>
                    </div>
                    <div>
                      {activeSubtab === "anual" ? "Faturamento Anual:" : "Faturamento Atual:"} <span className="font-black text-emerald-600">R$ {actualSales.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </form>
              )}
            </div>

            {/* ─── USER VIEW ─── */}
            {!isMaster && !personalStats && (
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm text-center flex flex-col items-center justify-center py-16">
                <Award size={40} className="text-slate-300 mb-2.5" />
                <h3 className="text-sm font-black text-slate-700 uppercase tracking-wider">Você não está em nenhuma equipe</h3>
                <p className="text-xs text-slate-400 mt-1 max-w-sm">Você não está cadastrado em nenhuma equipe de bônus no momento. Se achar que isso é um erro, por favor fale com o administrador master.</p>
              </div>
            )}
            {!isMaster && personalStats && (
              <div className="space-y-4">
                {/* Mensal / Anual Selector for Regular User */}
                <div className="flex bg-slate-100 p-1 rounded-xl w-fit">
                  <button
                    onClick={() => setViewMode("mensal")}
                    className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                      viewMode === "mensal" ? "bg-white text-amber-600 shadow-sm" : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    Visualização Mensal
                  </button>
                  <button
                    onClick={() => setViewMode("anual")}
                    className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                      viewMode === "anual" ? "bg-white text-amber-600 shadow-sm" : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    Visualização Anual
                  </button>
                </div>

                {viewMode === "anual" ? (
                  <div className="space-y-6">
                    {/* Yearly summary cards */}
                    {personalYearlySummary && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
                        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
                          <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500"></div>
                          <span className="block text-[9px] font-black text-slate-400 uppercase tracking-wider">Pontuação Anual Acumulada</span>
                          <span className="block text-lg font-black text-slate-800 mt-1">
                            {personalYearlySummary.remainingPoints} / {personalYearlySummary.maxPoints} pts
                          </span>
                          <span className="block text-[9px] text-slate-400 font-medium mt-1">
                            Média de {personalYearlySummary.maxPoints > 0 ? Math.round((personalYearlySummary.remainingPoints / personalYearlySummary.maxPoints) * 100) : 0}% de aproveitamento
                          </span>
                        </div>
                        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
                          <div className="absolute top-0 left-0 right-0 h-1 bg-emerald-500"></div>
                          <span className="block text-[9px] font-black text-slate-400 uppercase tracking-wider">Total Recebido (Pago)</span>
                          <span className="block text-lg font-black text-emerald-600 mt-1">
                            R$ {personalYearlySummary.totalPaid.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <span className="block text-[9px] text-slate-400 font-medium mt-1">Confirmados pelo administrador</span>
                        </div>
                        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
                          <div className="absolute top-0 left-0 right-0 h-1 bg-blue-550"></div>
                          <span className="block text-[9px] font-black text-slate-400 uppercase tracking-wider">Total Estimado Pendente</span>
                          <span className="block text-lg font-black text-blue-600 mt-1">
                            R$ {personalYearlySummary.totalPending.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <span className="block text-[9px] text-slate-400 font-medium mt-1">Bônus estimado aguardando fechamento/pagamento</span>
                        </div>
                      </div>
                    )}

                    {/* Month-by-month table */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col text-left">
                      <div className="px-5 py-4 border-b bg-slate-50 text-left shrink-0">
                        <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">
                          Desempenho e Pagamentos Mês a Mês ({selectedYear})
                        </h3>
                        <p className="text-[10px] text-slate-400 font-medium mt-0.5">
                          Histórico anual detalhado com score pessoal, bônus proporcional e status de pagamento.
                        </p>
                      </div>
                      
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs divide-y divide-slate-100">
                          <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-500">
                            <tr>
                              <th className="px-5 py-3">Mês</th>
                              <th className="px-5 py-3 text-center">Status</th>
                              <th className="px-5 py-3 text-center">Meta Global (Cia)</th>
                              <th className="px-5 py-3 text-center">Pontuação</th>
                              <th className="px-5 py-3 text-center">Presença</th>
                              <th className="px-5 py-3 text-right">Bônus Estimado</th>
                              <th className="px-5 py-3 text-center">Status Pagamento</th>
                              <th className="px-5 py-3 text-right">Valor Pago</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                            {personalYearlyMonths.map((m) => (
                              <tr key={m.mes} className="hover:bg-slate-50/50">
                                <td className="px-5 py-3 font-bold text-slate-800">{m.mesLabel}</td>
                                <td className="px-5 py-3 text-center">
                                  {m.isActive ? (
                                    <span className="bg-blue-50 border border-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[9px] font-bold">
                                      Ativo
                                    </span>
                                  ) : (
                                    <span className="bg-slate-50 border border-slate-200 text-slate-400 px-2 py-0.5 rounded-full text-[9px] font-bold">
                                      Inativo
                                    </span>
                                  )}
                                </td>
                                <td className="px-5 py-3 text-center">
                                  {m.isActive ? (
                                    m.goalPercentage >= 100 ? (
                                      <span className="bg-emerald-50 text-emerald-800 px-2.5 py-0.5 rounded-full text-[9px] font-black">
                                        Batida ({m.goalPercentage}%)
                                      </span>
                                    ) : (
                                      <span className="bg-amber-50 text-amber-800 px-2.5 py-0.5 rounded-full text-[9px] font-black">
                                        {m.goalPercentage}% Atingida
                                      </span>
                                    )
                                  ) : "-"}
                                </td>
                                <td className="px-5 py-3 text-center font-mono">
                                  {m.isActive ? `${m.remainingPoints} / ${m.maxPoints} pts` : "-"}
                                </td>
                                <td className="px-5 py-3 text-center font-mono text-[11px]">
                                  {m.isActive ? (
                                    m.isLider ? (
                                      <span className="text-[10px] text-slate-400 font-bold" title="Líderes de equipe não batem ponto e são isentos do bônus proporcional por presença.">
                                        Isento (Líder)
                                      </span>
                                    ) : (
                                      <span 
                                        className="cursor-help border-b border-dashed border-slate-400"
                                        title={`Faltas: ${m.parsed?.faltas || 0} | Atestados: ${m.parsed?.atestados || 0} | Férias: ${m.parsed?.ferias || 0}`}
                                      >
                                        {m.workedDays} / {m.totalUteis} d
                                      </span>
                                    )
                                  ) : "-"}
                                </td>
                                <td className="px-5 py-3 text-right font-black text-slate-800">
                                  {m.isActive ? `R$ ${m.expectedBonus.toFixed(2)}` : "-"}
                                </td>
                                <td className="px-5 py-3 text-center whitespace-nowrap">
                                  {!m.isActive ? (
                                    "-"
                                  ) : m.isPaid ? (
                                    <span className="bg-emerald-100 border border-emerald-250 text-emerald-800 px-2 py-0.5 rounded-full text-[9px] font-black">
                                      Pago
                                    </span>
                                  ) : (
                                    <span className="bg-slate-100 border border-slate-250 text-slate-500 px-2 py-0.5 rounded-full text-[9px] font-black">
                                      Pendente
                                    </span>
                                  )}
                                </td>
                                <td className="px-5 py-3 text-right font-mono font-black text-emerald-650">
                                  {m.isPaid ? `R$ ${m.paymentValue.toFixed(2)}` : "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Occurrences of the year */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col text-left">
                      <div className="px-5 py-4 border-b bg-slate-50 text-left shrink-0">
                        <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">
                          Histórico de Ocorrências do Ano Completo ({personalStats.occurrences.length})
                        </h3>
                      </div>

                      <div className="flex-1 overflow-x-auto">
                        {personalStats.occurrences.length === 0 ? (
                          <div className="p-8 text-center text-slate-400 flex flex-col justify-center items-center h-full">
                            <CheckCircle2 size={36} className="text-emerald-500 mb-2 opacity-60" />
                            <p className="text-xs font-bold">Excelente! Nenhuma ocorrência registrada.</p>
                            <p className="text-[10px] text-slate-500 mt-0.5">Seu saldo de bônus anual permanece integral.</p>
                          </div>
                        ) : (
                          <table className="w-full text-left text-xs divide-y divide-slate-100">
                            <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-500">
                              <tr>
                                <th className="px-5 py-3">Data</th>
                                <th className="px-5 py-3">Mês</th>
                                <th className="px-5 py-3">Ocorrência</th>
                                <th className="px-5 py-3 text-center">Débito</th>
                                <th className="px-5 py-3">Responsável</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                              {personalStats.occurrences.map(occ => (
                                <tr key={occ.id} className="hover:bg-slate-50/50">
                                  <td className="px-5 py-3 whitespace-nowrap font-mono">{occ.data}</td>
                                  <td className="px-5 py-3 font-semibold whitespace-nowrap">
                                    {MONTHS.find(m => m.value === occ.mes)?.label}
                                  </td>
                                  <td className="px-5 py-3 text-left">{occ.ocorrencia}</td>
                                  <td className="px-5 py-3 text-center text-red-650 font-black">-{occ.pontos_debitados} pt</td>
                                  <td className="px-5 py-3 font-mono text-[10px] text-slate-500">
                                    {occ.expand?.responsavel?.name || "SISTEMA"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                    {/* Personal statistics cards */}
                    <div className="md:col-span-1 space-y-4">
                      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden relative">
                        {/* Top Accent line */}
                        <div className="absolute top-0 left-0 right-0 h-1.5 bg-amber-500"></div>
                        <div className="p-5 text-left">
                          <span className="block text-[10px] font-black uppercase text-slate-400 tracking-wider">
                            Meu Score do Mês
                          </span>
                          
                          {!personalStats.isActive ? (
                            <div className="py-12 text-center text-slate-400 flex flex-col justify-center items-center h-full">
                              <Info size={32} className="text-amber-500 mb-2 opacity-80" />
                              <p className="text-xs font-black text-slate-700 uppercase tracking-wider">Não Elegível no Período</p>
                              <p className="text-[10px] text-slate-450 mt-1.5 leading-normal">
                                Sua data de admissão é <strong>{formatAdmissao(personalStats.dataAdmissao)}</strong>.
                                <br />
                                A pontuação e o bônus só iniciam no primeiro mês completo trabalhado.
                              </p>
                            </div>
                          ) : (
                            <>
                              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                                <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                                  <span className="block text-[9px] font-bold text-slate-400 uppercase">Inicial Mês</span>
                                  <span className="text-lg font-black text-slate-800">{personalStats.maxPoints}</span>
                                </div>
                                <div className="bg-red-50/50 p-2.5 rounded-xl border border-red-100/50">
                                  <span className="block text-[9px] font-bold text-red-500 uppercase">Debitado Mês</span>
                                  <span className="text-lg font-black text-red-650">-{personalStats.pointsLost}</span>
                                </div>
                                <div className="bg-emerald-50/50 p-2.5 rounded-xl border border-emerald-100/50">
                                  <span className="block text-[9px] font-bold text-emerald-600 uppercase">Saldo Mês</span>
                                  <span className="text-lg font-black text-emerald-750">{personalStats.remainingPoints}</span>
                                </div>
                              </div>

                              <div className="mt-4 pt-4 border-t border-slate-150 flex items-center justify-between">
                                <div>
                                  <span className="block text-[9px] font-bold text-slate-400 uppercase">Bônus Habilitado</span>
                                  <span className="text-xs font-black text-slate-700">{personalStats.activeBonusPct}%</span>
                                </div>
                                <div className="text-right">
                                  <span className="block text-[9px] font-bold text-slate-400 uppercase">Valor Estimado</span>
                                  <span className="text-sm font-black text-amber-600">R$ {personalStats.expectedBonus.toFixed(2)}</span>
                                </div>
                              </div>

                              {/* Controle dos pagamentos de bônus feitos */}
                              <div className="mt-3 pt-3 border-t border-slate-150 flex items-center justify-between">
                                <div>
                                  <span className="block text-[9px] font-bold text-slate-400 uppercase">Status do Pagamento</span>
                                  <div className="mt-1">
                                    {personalMonthlyPayment?.status === "pago" ? (
                                      <span className="bg-emerald-100 border border-emerald-250 text-emerald-800 px-2 py-0.5 rounded-full text-[9px] font-black">
                                        Pago ({new Date(personalMonthlyPayment.data_pagamento).toLocaleDateString("pt-BR")})
                                      </span>
                                    ) : (
                                      <span className="bg-slate-100 border border-slate-250 text-slate-500 px-2 py-0.5 rounded-full text-[9px] font-black">
                                        Pendente
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {personalMonthlyPayment?.status === "pago" && (
                                  <div className="text-right">
                                    <span className="block text-[9px] font-bold text-slate-400 uppercase">Valor Pago</span>
                                    <span className="text-xs font-black text-emerald-600">
                                      R$ {personalMonthlyPayment.valor_pago.toFixed(2)}
                                    </span>
                                  </div>
                                )}
                              </div>

                              <p className="text-[9px] text-slate-400 mt-3 italic leading-normal">
                                Bônus Máximo possível para sua função: R$ {personalStats.maxBonus.toFixed(2)} (Score de {personalStats.maxPoints} pts).
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Occurrence List Table */}
                    <div className="md:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                      <div className="px-5 py-4 border-b bg-slate-50 text-left shrink-0">
                        <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">Histórico de Ocorrências do Mês</h3>
                      </div>

                      <div className="flex-1 overflow-x-auto">
                        {!personalStats.isActive ? (
                          <div className="p-8 text-center text-slate-400 flex flex-col justify-center items-center h-full">
                            <Info size={36} className="text-slate-350 mb-2 opacity-65" />
                            <p className="text-xs font-bold">Período Inativo</p>
                            <p className="text-[10px] text-slate-500 mt-0.5">Nenhuma ocorrência é contabilizada antes de sua data oficial de início de bônus.</p>
                          </div>
                        ) : personalStats.occurrences.length === 0 ? (
                          <div className="p-8 text-center text-slate-400 flex flex-col justify-center items-center h-full">
                            <CheckCircle2 size={36} className="text-emerald-500 mb-2 opacity-60" />
                            <p className="text-xs font-bold">Excelente! Nenhuma ocorrência registrada.</p>
                            <p className="text-[10px] text-slate-500 mt-0.5">Seu saldo de bônus permanece integral.</p>
                          </div>
                        ) : (
                          <table className="w-full text-left text-xs divide-y divide-slate-100">
                            <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-500">
                              <tr>
                                <th className="px-5 py-3">Data</th>
                                <th className="px-5 py-3">Ocorrência</th>
                                <th className="px-5 py-3 text-center">Débito</th>
                                <th className="px-5 py-3">Responsável</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                              {personalStats.occurrences.map(occ => (
                                <tr key={occ.id} className="hover:bg-slate-50/50">
                                  <td className="px-5 py-3 whitespace-nowrap font-mono">{occ.data}</td>
                                  <td className="px-5 py-3 text-left">
                                    {occ.colaborador !== user?.id && occ.expand?.colaborador?.name && (
                                      <span className="bg-blue-50 border border-blue-100 text-blue-700 font-bold px-1.5 py-0.5 rounded text-[9px] mr-1.5 uppercase">
                                        {occ.expand.colaborador.name.split(" ")[0]}
                                      </span>
                                    )}
                                    {occ.ocorrencia}
                                  </td>
                                  <td className="px-5 py-3 text-center text-red-650 font-black">-{occ.pontos_debitados} pt</td>
                                  <td className="px-5 py-3 font-mono text-[10px] text-slate-500">
                                    {occ.expand?.responsavel?.name || "SISTEMA"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

                {/* ─── ADMIN VIEWS (MASTER ROLE) ─── */}
                {isMaster && (
                  <>
                    {/* A. DASHBOARD GERAL SUBTAB */}
                    {activeSubtab === "dashboard" && (
                      <div className="w-full">
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col w-full">
                      <div className="px-5 py-4 border-b bg-slate-50 flex justify-between items-center shrink-0 text-left">
                        <div>
                          <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">Pontuação dos Colaboradores</h3>
                          <p className="text-[10px] text-slate-400 font-medium">Bônus estimados e saldo de pontos ativos</p>
                        </div>
                      </div>

                      <div className="flex-1 overflow-x-auto">
                        <table className="w-full text-left text-xs divide-y divide-slate-100">
                          <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-500">
                            <tr>
                              <th className="px-5 py-3">Colaborador</th>
                              <th className="px-5 py-3 text-center">Score Inicial</th>
                              <th className="px-5 py-3 text-center">Pontos Debitados</th>
                              <th className="px-5 py-3 text-center">Saldo Restante</th>
                              <th className="px-5 py-3 text-center">% Bônus</th>
                              <th className="px-5 py-3 text-center">Presença</th>
                              <th className="px-5 py-3 text-right">Valor Estimado</th>
                              <th className="px-5 py-3 text-right">Ações</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                            {collaboratorsSummary.map(colab => (
                              <tr key={colab.id} className="hover:bg-slate-50/50">
                                <td className="px-5 py-3 whitespace-nowrap">
                                  <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center border text-[10px] text-slate-500 font-bold overflow-hidden shrink-0">
                                      {colab.avatar ? (
                                        <img src={pb.files.getURL(colab, colab.avatar)} className="w-full h-full object-cover" alt="" />
                                      ) : (
                                        colab.name.slice(0,2).toUpperCase()
                                      )}
                                    </div>
                                    <div className="text-left">
                                      <span className="font-bold text-slate-800">{colab.name}</span>
                                      <p className="text-[9px] text-slate-400 font-mono">@{colab.username}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-5 py-3 text-center font-mono">{colab.isActive ? `${colab.maxPoints} pts` : "-"}</td>
                                <td className="px-5 py-3 text-center font-mono text-red-650">{colab.isActive ? `-${colab.pointsLost} pts` : "-"}</td>
                                <td className="px-5 py-3 text-center font-mono font-black text-slate-800">{colab.isActive ? `${colab.remainingPoints} pts` : "-"}</td>
                                <td className="px-5 py-3 text-center font-mono">{colab.isActive ? `${colab.activeBonusPct}%` : "-"}</td>
                                <td className="px-5 py-3 text-center font-mono text-[11px]">
                                  {colab.isActive ? (
                                    colab.isLider ? (
                                      <span className="text-[10px] text-slate-400 font-bold" title="Líderes de equipe não batem ponto e são isentos do bônus proporcional por presença.">
                                        Isento (Líder)
                                      </span>
                                    ) : (
                                      <span 
                                        className="cursor-help border-b border-dashed border-slate-400"
                                        title={`Faltas: ${colab.totalFaltas} | Atestados: ${colab.totalAtestados} | Férias: ${colab.totalFerias}`}
                                      >
                                        {colab.totalTrabalhados} / {colab.totalUteis} d
                                      </span>
                                    )
                                  ) : "-"}
                                </td>
                                <td className="px-5 py-3 text-right font-black text-amber-600 whitespace-nowrap">
                                  {colab.isActive ? (
                                    `R$ ${colab.expectedBonus.toFixed(2)}`
                                  ) : (
                                    <span className="text-[10px] text-slate-400 font-bold" title={`Admissão: ${formatAdmissao(colab.dataAdmissao)}`}>
                                      Não Elegível
                                    </span>
                                  )}
                                </td>
                                <td className="px-5 py-3 text-right">
                                  <button 
                                    onClick={() => setDetailColab(colab)}
                                    className="p-1 hover:bg-slate-100 rounded text-blue-600 transition-colors"
                                    title="Ver Histórico"
                                  >
                                    <ChevronRight size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
 
                {/* E. ANO COMPLETO SUBTAB */}
                {activeSubtab === "anual" && (
                  <div className="space-y-4 text-left w-full">
                    {/* Main Area: Seletor de Colaborador e Detalhes Anuais */}
                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm text-left">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                        Selecione o Colaborador para Visualizar Pontuação e Ocorrências do Ano
                      </label>
                      <select
                        value={selectedAnnualColabId}
                        onChange={(e) => setSelectedAnnualColabId(e.target.value)}
                        className="w-full p-2.5 border border-slate-200 bg-slate-50 rounded-xl font-bold text-slate-700 text-xs outline-none focus:border-amber-500 focus:bg-white transition-all shadow-inner font-bold"
                      >
                        <option value="">Selecione o Colaborador...</option>
                        {collaboratorsSummary.map(colab => {
                          let statusText = "";
                          if (!colab.isActive) {
                            statusText = " (Não Elegível)";
                          } else if (colab.activeMonthsCount < 12) {
                            statusText = ` (Proporcional: ${colab.activeMonthsCount} meses)`;
                          }
                          return (
                            <option key={colab.id} value={colab.id}>
                              {colab.name} (@{colab.username}){statusText}
                            </option>
                          );
                        })}
                      </select>
                    </div>
 
                    {/* Se colaborador selecionado, exibe os cards de score e tabela de ocorrências */}
                    {selectedAnnualColabId ? (() => {
                      const colabData = collaboratorsSummary.find(c => c.id === selectedAnnualColabId);
                      if (!colabData) return null;
                      return (
                        <div className="space-y-4">
                          {/* Score Card Anual */}
                          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
                            <div className="absolute top-0 left-0 right-0 h-1.5 bg-amber-500"></div>
                            <h4 className="text-xs font-black text-slate-700 uppercase tracking-wider mb-3">
                              Desempenho Anual Acumulado: {colabData.name}
                            </h4>
                            
                            {!colabData.isActive ? (
                              <div className="py-12 text-center text-slate-400 flex flex-col justify-center items-center h-full">
                                <Info size={32} className="text-amber-500 mb-2 opacity-80" />
                                <p className="text-xs font-black text-slate-700 uppercase tracking-wider">Não Elegível no Ano</p>
                                <p className="text-[10px] text-slate-450 mt-1.5 leading-normal">
                                  Admitido em <strong>{formatAdmissao(colabData.dataAdmissao)}</strong>.
                                  <br />
                                  Não há meses trabalhados completos no ano de {selectedYear}.
                                </p>
                              </div>
                            ) : (
                              <>
                                <div className="grid grid-cols-3 gap-3 text-center">
                                  <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                                    <span className="block text-[9px] font-bold text-slate-400 uppercase">Inicial Anual</span>
                                    <span className="text-lg font-black text-slate-800">{colabData.maxPoints}</span>
                                  </div>
                                  <div className="bg-red-50/50 p-2.5 rounded-xl border border-red-100/50">
                                    <span className="block text-[9px] font-bold text-red-500 uppercase">Debitado no Ano</span>
                                    <span className="text-lg font-black text-red-650">-{colabData.pointsLost}</span>
                                  </div>
                                  <div className="bg-emerald-50/50 p-2.5 rounded-xl border border-emerald-100/50">
                                    <span className="block text-[9px] font-bold text-emerald-600 uppercase">Saldo Anual</span>
                                    <span className="text-lg font-black text-emerald-750">{colabData.remainingPoints}</span>
                                  </div>
                                </div>
 
                                <div className="mt-4 pt-4 border-t border-slate-150 flex items-center justify-between">
                                  <div>
                                    <span className="block text-[9px] font-bold text-slate-400 uppercase">Bônus Habilitado</span>
                                    <span className="text-xs font-black text-slate-700">{colabData.activeBonusPct}%</span>
                                  </div>
                                  <div className="text-right">
                                    <span className="block text-[9px] font-bold text-slate-400 uppercase">Bônus Anual Estimado</span>
                                    <span className="text-sm font-black text-amber-600">R$ {colabData.expectedBonus.toFixed(2)}</span>
                                  </div>
                                </div>
 
                                <p className="text-[9px] text-slate-400 mt-3 italic leading-normal text-left">
                                  Bônus Anual Máximo possível no período ativo: R$ {colabData.maxBonusPeriod.toFixed(2)} (Score de {colabData.maxPoints} pts). Proporcional a {colabData.activeMonthsCount} meses ativos.
                                </p>
                              </>
                            )}
                          </div>
 
                          {/* Tabela de Ocorrências do Ano */}
                          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                            <div className="px-5 py-4 border-b bg-slate-50 text-left shrink-0">
                              <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">Histórico de Ocorrências do Ano ({colabData.occurrences.length})</h3>
                            </div>
 
                            <div className="flex-1 overflow-x-auto">
                              {!colabData.isActive ? (
                                <div className="p-8 text-center text-slate-400 flex flex-col justify-center items-center h-full">
                                  <Info size={36} className="text-slate-350 mb-2 opacity-65" />
                                  <p className="text-xs font-bold">Período Inativo</p>
                                  <p className="text-[10px] text-slate-500 mt-0.5">Nenhuma ocorrência é contabilizada antes de sua data oficial de início de bônus.</p>
                                </div>
                              ) : colabData.occurrences.length === 0 ? (
                                <div className="p-8 text-center text-slate-400 flex flex-col justify-center items-center h-full">
                                  <CheckCircle2 size={36} className="text-emerald-500 mb-2 opacity-60" />
                                  <p className="text-xs font-bold">Excelente! Nenhuma ocorrência registrada para este colaborador no ano.</p>
                                </div>
                              ) : (
                                <table className="w-full text-left text-xs divide-y divide-slate-100">
                                  <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-500">
                                    <tr>
                                      <th className="px-5 py-3">Data</th>
                                      <th className="px-5 py-3">Ocorrência / Motivo</th>
                                      <th className="px-5 py-3 text-center">Débito</th>
                                      <th className="px-5 py-3">Responsável</th>
                                      <th className="px-5 py-3 text-right">Ações</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                                    {colabData.occurrences.map(occ => (
                                      <tr key={occ.id} className="hover:bg-slate-50/50">
                                        <td className="px-5 py-3 whitespace-nowrap font-mono">{occ.data}</td>
                                        <td className="px-5 py-3 text-left">
                                          {occ.colaborador !== selectedAnnualColabId && occ.expand?.colaborador?.name && (
                                            <span className="bg-blue-50 border border-blue-100 text-blue-700 font-bold px-1.5 py-0.5 rounded text-[9px] mr-1.5 uppercase">
                                              {occ.expand.colaborador.name.split(" ")[0]}
                                            </span>
                                          )}
                                          {occ.ocorrencia}
                                        </td>
                                        <td className="px-5 py-3 text-center text-red-650 font-black">-{occ.pontos_debitados} pt</td>
                                        <td className="px-5 py-3 font-mono text-[10px] text-slate-500">
                                          {occ.expand?.responsavel?.name || "SISTEMA"}
                                        </td>
                                        <td className="px-5 py-3 text-right">
                                          <button
                                            onClick={() => handleDeleteOccurrence(occ.id)}
                                            className="p-1 hover:bg-red-50 text-red-600 rounded transition-colors"
                                            title="Excluir Lançamento"
                                          >
                                            <Trash2 size={15} />
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })() : (
                      <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm text-center py-16 text-slate-400">
                        <User size={40} className="mx-auto mb-2.5 opacity-60 text-slate-300" />
                        <p className="text-xs font-bold uppercase tracking-wider">Nenhum Colaborador Selecionado</p>
                        <p className="text-[11px] text-slate-400 mt-1">Selecione um colaborador acima para visualizar seu desempenho e ocorrências acumuladas do ano completo.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* F. CONTROLE DE PAGAMENTOS SUBTAB */}
                {activeSubtab === "pagamentos" && (
                  <div className="space-y-6 text-left">
                    
                    {/* Status Banner */}
                    <div className={`p-4 rounded-2xl border flex items-center justify-between shadow-sm relative overflow-hidden ${
                      companyGoalPercentage >= 100 
                        ? "bg-emerald-50/50 border-emerald-200 text-emerald-800" 
                        : "bg-amber-50/50 border-amber-200 text-amber-800"
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center border font-black text-sm ${
                          companyGoalPercentage >= 100 
                            ? "bg-emerald-100 border-emerald-300 text-emerald-700" 
                            : "bg-amber-100 border-amber-300 text-amber-700"
                        }`}>
                          {companyGoalPercentage >= 100 ? "✓" : "!"}
                        </div>
                        <div>
                          <h4 className="font-extrabold text-xs uppercase tracking-wider">
                            {companyGoalPercentage >= 100 
                              ? "Meta Global Atingida! Bônus Habilitado." 
                              : "Meta Global Não Atingida"}
                          </h4>
                          <p className="text-[10px] font-medium text-slate-500 mt-0.5">
                            {companyGoalPercentage >= 100 
                              ? "A meta de faturamento da empresa foi batida no mês selecionado. Os pagamentos de bônus estão ativos."
                              : "O faturamento da empresa ficou abaixo da meta. Por padrão, o bônus não deve ser distribuído, exceto se houver aprovação administrativa."}
                          </p>
                        </div>
                      </div>
                      <span className="font-black text-sm">{companyGoalPercentage}% Atingido</span>
                    </div>

                    {/* Resumos Financeiros Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden text-left">
                        <div className="absolute top-0 left-0 right-0 h-1 bg-slate-300"></div>
                        <span className="block text-[9px] font-black text-slate-400 uppercase tracking-wider">Total Estimado no Mês</span>
                        <span className="block text-lg font-black text-slate-800 mt-1">R$ {paymentsTotals.totalEstimado.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden text-left">
                        <div className="absolute top-0 left-0 right-0 h-1 bg-emerald-500"></div>
                        <span className="block text-[9px] font-black text-slate-400 uppercase tracking-wider">Total Pago (Confirmado)</span>
                        <span className="block text-lg font-black text-emerald-600 mt-1">R$ {paymentsTotals.totalPago.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden text-left">
                        <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500"></div>
                        <span className="block text-[9px] font-black text-slate-400 uppercase tracking-wider">Total Pendente</span>
                        <span className="block text-lg font-black text-amber-600 mt-1">R$ {paymentsTotals.totalPendente.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>

                    {/* Tabela de Pagamentos */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                      <div className="px-5 py-4 border-b bg-slate-50 text-left">
                        <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">Controle de Pagamentos por Colaborador</h3>
                        <p className="text-[10px] text-slate-400 font-medium">Selecione o mês desejado no cabeçalho para gerenciar os bônus daquele período</p>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs divide-y divide-slate-100 font-semibold text-slate-700">
                          <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-500">
                            <tr>
                              <th className="px-5 py-3">Colaborador</th>
                              <th className="px-5 py-3 text-center">Score / Saldo</th>
                              <th className="px-5 py-3 text-center">% Bônus</th>
                              <th className="px-5 py-3 text-center">Presença</th>
                              <th className="px-5 py-3 text-right">Bônus Estimado</th>
                              <th className="px-5 py-3 text-center">Status Pagamento</th>
                              <th className="px-5 py-3 text-right">Ações</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {collaboratorsSummary.map(colab => {
                              const paymentRecord = payments.find(p => p.colaborador === colab.id && p.status === "pago");
                              return (
                                <tr key={colab.id} className="hover:bg-slate-50/50">
                                  <td className="px-5 py-3 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center border text-[10px] text-slate-500 font-bold overflow-hidden shrink-0">
                                        {colab.avatar ? (
                                          <img src={pb.files.getURL(colab, colab.avatar)} className="w-full h-full object-cover" alt="" />
                                        ) : (
                                          colab.name.slice(0,2).toUpperCase()
                                        )}
                                      </div>
                                      <div className="text-left">
                                        <span className="font-bold text-slate-800">{colab.name}</span>
                                        <p className="text-[9px] text-slate-400 font-mono">@{colab.username}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-5 py-3 text-center font-mono">
                                    {colab.isActive ? `${colab.remainingPoints} / ${colab.maxPoints} pts` : "-"}
                                  </td>
                                  <td className="px-5 py-3 text-center font-mono">
                                    {colab.isActive ? `${colab.activeBonusPct}%` : "-"}
                                  </td>
                                  <td className="px-5 py-3 text-center font-mono text-[11px]">
                                    {colab.isActive ? (
                                      colab.isLider ? (
                                        <span className="text-[10px] text-slate-400 font-bold" title="Líderes de equipe não batem ponto e são isentos do bônus proporcional por presença.">
                                          Isento (Líder)
                                        </span>
                                      ) : (
                                        <span 
                                          className="cursor-help border-b border-dashed border-slate-400"
                                          title={`Faltas: ${colab.totalFaltas} | Atestados: ${colab.totalAtestados} | Férias: ${colab.totalFerias}`}
                                        >
                                          {colab.totalTrabalhados} / {colab.totalUteis} d
                                        </span>
                                      )
                                    ) : "-"}
                                  </td>
                                  <td className="px-5 py-3 text-right font-black text-slate-700">
                                    {colab.isActive ? `R$ ${colab.expectedBonus.toFixed(2)}` : "-"}
                                  </td>
                                  <td className="px-5 py-3 text-center whitespace-nowrap">
                                    {!colab.isActive ? (
                                      <span className="bg-slate-50 border border-slate-200 text-slate-400 px-2 py-0.5 rounded-full text-[9px] font-bold">
                                        Período Inativo
                                      </span>
                                    ) : paymentRecord ? (
                                      <span className="bg-emerald-100 border border-emerald-250 text-emerald-800 px-2 py-0.5 rounded-full text-[9px] font-black">
                                        Pago ({new Date(paymentRecord.data_pagamento).toLocaleDateString("pt-BR")})
                                      </span>
                                    ) : (
                                      <span className="bg-slate-100 border border-slate-250 text-slate-500 px-2 py-0.5 rounded-full text-[9px] font-black">
                                        Pendente
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-5 py-3 text-right whitespace-nowrap">
                                    {!colab.isActive ? (
                                      <span className="text-[10px] text-slate-450 italic font-bold">Sem bônus</span>
                                    ) : paymentRecord ? (
                                      <button
                                        type="button"
                                        onClick={() => handleTogglePayment(colab.id, colab.expectedBonus, "pendente")}
                                        className="px-2.5 py-1 text-[10px] bg-red-50 hover:bg-red-100 rounded font-black text-red-600 transition-colors"
                                      >
                                        Desfazer Pagamento
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => handleTogglePayment(colab.id, colab.expectedBonus, "pago")}
                                        className="px-2.5 py-1 text-[10px] bg-emerald-50 hover:bg-emerald-100 rounded font-black text-emerald-600 transition-colors"
                                      >
                                        Confirmar Pagamento
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            {collaboratorsSummary.length === 0 && (
                              <tr>
                                <td colSpan="7" className="py-8 text-center text-slate-400">
                                  Nenhum colaborador participando da pontuação.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* B. LANÇAR OCORRÊNCIA SUBTAB */}
                {activeSubtab === "lancar" && (
                  <div className="max-w-xl mx-auto bg-white p-6 rounded-2xl border border-slate-200 shadow-sm text-left">
                    <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider mb-5 flex items-center gap-1.5">
                      <Plus size={16} className="text-amber-500" />
                      Lançamento de Ocorrência
                    </h3>

                    <form onSubmit={handleLaunchOccurrence} className="space-y-4 font-bold text-xs">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Colaborador *</label>
                        <select
                          className="w-full p-2.5 border border-slate-200 bg-slate-50 rounded-xl font-bold text-slate-700 text-xs outline-none focus:border-amber-500 focus:bg-white transition-all shadow-inner font-bold"
                          value={formColabId}
                          onChange={(e) => setFormColabId(e.target.value)}
                          required
                        >
                          <option value="">Selecione o Colaborador...</option>
                          <optgroup label="Lançar para Equipes Inteiras">
                            {colabConfigs.filter(c => c.is_lider).map(leaderConfig => {
                              const leaderUser = collaborators.find(u => u.id === leaderConfig.colaborador);
                              if (!leaderUser) return null;
                              return (
                                <option key={`team_${leaderConfig.colaborador}`} value={`team:${leaderConfig.colaborador}`}>
                                  Toda a Equipe de {leaderUser.name}
                                </option>
                              );
                            })}
                          </optgroup>
                          <optgroup label="Colaboradores Individuais">
                            {collaborators.filter(c => colabConfigs.some(cfg => cfg.colaborador === c.id)).map(c => (
                              <option key={c.id} value={c.id}>{c.name} (@{c.username})</option>
                            ))}
                          </optgroup>
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Data da Ocorrência *</label>
                          <input
                            type="date"
                            className="w-full p-2.5 border border-slate-200 bg-slate-50 rounded-xl font-bold text-slate-700 text-xs outline-none focus:border-amber-500 focus:bg-white transition-all shadow-inner font-mono"
                            value={formDate}
                            onChange={(e) => setFormDate(e.target.value)}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Pontos a Debitar *</label>
                          <input
                            type="number"
                            min="1"
                            max="20"
                            className="w-full p-2.5 border border-slate-200 bg-slate-50 rounded-xl font-bold text-slate-700 text-xs outline-none focus:border-amber-500 focus:bg-white transition-all shadow-inner text-center font-mono"
                            value={formPoints}
                            onChange={(e) => setFormPoints(e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Detalhes da Ocorrência / Motivo *</label>
                        <textarea
                          rows={3}
                          placeholder="Descreva o que ocorreu (ex: Pedido 12345 enviado para endereço errado, erro na gravação, etc.)"
                          className="w-full p-2.5 border border-slate-200 bg-slate-50 rounded-xl font-bold text-slate-700 text-xs outline-none focus:border-amber-500 focus:bg-white transition-all shadow-inner font-normal"
                          value={formDescription}
                          onChange={(e) => setFormDescription(e.target.value)}
                          required
                        />
                      </div>

                      <div className="pt-2">
                        <button
                          type="submit"
                          disabled={submittingOccurrence}
                          className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-xs rounded-xl shadow-md transition-all uppercase active:scale-98 flex items-center justify-center gap-1.5"
                        >
                          {submittingOccurrence ? (
                            <>
                              <Loader2 className="animate-spin" size={14} /> Lançando...
                            </>
                          ) : (
                            "Registrar Débito de Pontos"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* C. GERENCIAR OCORRÊNCIAS SUBTAB */}
                {activeSubtab === "gerenciar" && (
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                    <div className="px-5 py-4 border-b bg-slate-50 flex flex-col sm:flex-row justify-between items-center gap-3 shrink-0 text-left">
                      <div>
                        <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">Lançamentos de Ocorrência no Mês</h3>
                        <p className="text-[10px] text-slate-400 font-medium">Histórico geral de auditoria e exclusão</p>
                      </div>

                      <div className="relative w-full sm:w-64 font-bold text-xs">
                        <input
                          type="text"
                          placeholder="Buscar por colaborador ou motivo..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-3 pr-8 py-2 border border-slate-200 rounded-lg outline-none focus:border-amber-500 text-xs font-medium bg-slate-50 focus:bg-white transition-all shadow-inner"
                        />
                      </div>
                    </div>

                    <div className="flex-1 overflow-x-auto">
                      {filteredOccurrences.length === 0 ? (
                        <div className="p-8 text-center text-slate-400">
                          <Info size={36} className="mx-auto mb-2 text-slate-300" />
                          <p className="text-xs font-bold">Nenhuma ocorrência encontrada para o filtro selecionado.</p>
                        </div>
                      ) : (
                        <table className="w-full text-left text-xs divide-y divide-slate-100">
                          <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-500">
                            <tr>
                              <th className="px-5 py-3">Data</th>
                              <th className="px-5 py-3">Colaborador</th>
                              <th className="px-5 py-3">Ocorrência / Motivo</th>
                              <th className="px-5 py-3 text-center">Débito</th>
                              <th className="px-5 py-3">Responsável</th>
                              <th className="px-5 py-3 text-right">Ações</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                            {filteredOccurrences.map(occ => (
                              <tr key={occ.id} className="hover:bg-slate-50/50">
                                <td className="px-5 py-3 whitespace-nowrap font-mono">{occ.data}</td>
                                <td className="px-5 py-3 font-bold text-slate-800">
                                  {occ.expand?.colaborador?.name || "N/D"}
                                </td>
                                <td className="px-5 py-3 max-w-xs truncate" title={occ.ocorrencia}>{occ.ocorrencia}</td>
                                <td className="px-5 py-3 text-center text-red-650 font-black">-{occ.pontos_debitados} pt</td>
                                <td className="px-5 py-3 font-mono text-[10px] text-slate-500">
                                  {occ.expand?.responsavel?.name || "SISTEMA"}
                                </td>
                                <td className="px-5 py-3 text-right">
                                  <button
                                    onClick={() => handleDeleteOccurrence(occ.id)}
                                    className="p-1 hover:bg-red-50 text-red-600 rounded transition-colors"
                                    title="Excluir Lançamento"
                                  >
                                    <Trash2 size={15} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                )}

                {/* D. CONFIGURAR EQUIPES SUBTAB */}
                {activeSubtab === "equipes" && (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-fade-in text-left">
                    
                    {/* Left: Equipes Cadastradas */}
                    <div className="lg:col-span-5 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm text-left">
                      <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider mb-4">
                        Estrutura de Equipes
                      </h3>
                      
                      <div className="space-y-4">
                        {colabConfigs.filter(c => c.is_lider).map(leaderConfig => {
                          const leaderUser = collaborators.find(u => u.id === leaderConfig.colaborador);
                          if (!leaderUser) return null;
                          
                          const teamMembers = colabConfigs.filter(c => c.lider === leaderConfig.colaborador);
                          
                          return (
                            <div key={leaderConfig.id} className="bg-slate-50 p-4 rounded-xl border border-slate-200/60">
                              <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                                <span className="font-extrabold text-slate-800 text-xs flex items-center gap-1.5">
                                  <User size={14} className="text-amber-500" />
                                  Equipe de {leaderUser.name} (Líder)
                                </span>
                                <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold">
                                  {teamMembers.length} membros
                                </span>
                              </div>
                              
                              <div className="mt-2.5 space-y-1.5">
                                {teamMembers.length === 0 ? (
                                  <p className="text-[10px] text-slate-400 font-medium italic">Nenhum membro nesta equipe.</p>
                                ) : (
                                  teamMembers.map(memberConfig => {
                                    const memberUser = collaborators.find(u => u.id === memberConfig.colaborador);
                                    if (!memberUser) return null;
                                    return (
                                      <div key={memberConfig.id} className="flex justify-between items-center bg-white px-3 py-1.5 rounded-lg border border-slate-100 text-xs">
                                        <span className="font-semibold text-slate-700">{memberUser.name}</span>
                                        <span className="text-[10px] text-slate-400 font-mono">@{memberUser.username}</span>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                          );
                        })}

                        {colabConfigs.filter(c => c.is_lider).length === 0 && (
                          <div className="py-8 text-center text-slate-400">
                            <Info size={24} className="mx-auto mb-1 opacity-60" />
                            <p className="text-xs font-bold">Nenhum líder configurado.</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: Tabela de Participantes e Bônus */}
                    <div className="lg:col-span-7 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                      <div className="px-5 py-4 border-b bg-slate-50 flex justify-between items-center text-left">
                        <div>
                          <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">Configuração de Participação & Bônus</h3>
                          <p className="text-[10px] text-slate-400 font-medium">Defina quem participa, os líderes e o bônus base de cada um</p>
                        </div>
                      </div>

                      {editingColab ? (
                        /* Formulário de Edição */
                        <div className="p-5 border-b border-slate-150 bg-amber-50/20 text-left space-y-4">
                          <h4 className="text-xs font-black text-slate-700 uppercase tracking-wider">
                            Editar Configuração: {collaborators.find(u => u.id === editingColab)?.name}
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 font-bold text-xs text-left">
                            <div>
                              <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Tipo de Membro</label>
                              <select 
                                className="w-full p-2 border rounded-lg bg-white outline-none focus:border-amber-500 font-bold"
                                value={editIsLider ? "lider" : editLider ? "membro" : "excluido"}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === "lider") {
                                    setEditIsLider(true);
                                    setEditLider("");
                                  } else if (val === "membro") {
                                    setEditIsLider(false);
                                    const leaders = colabConfigs.filter(c => c.is_lider && c.colaborador !== editingColab);
                                    setEditLider(leaders[0]?.colaborador || "");
                                  } else {
                                    setEditIsLider(false);
                                    setEditLider("");
                                  }
                                }}
                              >
                                <option value="lider">Líder de Equipe</option>
                                <option value="membro">Membro de Equipe</option>
                                <option value="excluido">Não Participa (Sem Equipe)</option>
                              </select>
                            </div>
 
                            {/* Dropdown de Líder (se for membro) */}
                            {!editIsLider && (
                              <div>
                                <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Selecionar Líder</label>
                                <select 
                                  className="w-full p-2 border rounded-lg bg-white outline-none focus:border-amber-500 font-bold disabled:bg-slate-100 disabled:text-slate-400"
                                  value={editLider}
                                  onChange={(e) => setEditLider(e.target.value)}
                                  disabled={!colabConfigs.some(c => c.is_lider && c.colaborador !== editingColab)}
                                >
                                  <option value="">Selecione...</option>
                                  {colabConfigs.filter(c => c.is_lider && c.colaborador !== editingColab).map(lConfig => {
                                    const lUser = collaborators.find(u => u.id === lConfig.colaborador);
                                    return lUser ? (
                                      <option key={lConfig.id} value={lConfig.colaborador}>{lUser.name}</option>
                                    ) : null;
                                  })}
                                </select>
                              </div>
                            )}
 
                            <div>
                              <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Bônus Base (R$)</label>
                              <input 
                                type="number" 
                                className="w-full p-2 border rounded-lg bg-white outline-none focus:border-amber-500 font-bold font-mono"
                                value={editBonusBase}
                                onChange={(e) => setEditBonusBase(Number(e.target.value))}
                              />
                            </div>

                            <div>
                              <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Data de Admissão</label>
                              <input 
                                type="date" 
                                className="w-full p-2 border rounded-lg bg-white outline-none focus:border-amber-500 font-bold font-mono"
                                value={editDataAdmissao}
                                onChange={(e) => setEditDataAdmissao(e.target.value)}
                              />
                            </div>
                          </div>
 
                          <div className="flex justify-end gap-2 text-xs">
                            <button 
                              onClick={() => setEditingColab(null)}
                              className="px-3 py-1.5 text-slate-500 font-bold hover:bg-slate-100 rounded-lg"
                            >
                              Cancelar
                            </button>
                            <button 
                              onClick={async () => {
                                const targetUser = collaborators.find(u => u.id === editingColab);
                                const liderUser = collaborators.find(u => u.id === editLider);
                                
                                try {
                                  const updated = await pontuacaoService.saveColaboradorConfig(editingColab, {
                                    is_lider: editIsLider,
                                    lider: editLider || null,
                                    bonus_base: editBonusBase,
                                    data_admissao: editDataAdmissao || null,
                                    colaboradorName: targetUser?.name,
                                    liderName: liderUser?.name
                                  });
                                  setColabConfigs(prev => {
                                    const idx = prev.findIndex(c => c.colaborador === editingColab);
                                    if (idx >= 0) {
                                      const next = [...prev];
                                      next[idx] = updated;
                                      return next;
                                    } else {
                                      return [...prev, updated];
                                    }
                                  });
                                  
                                  if (updated && updated.isLocal) {
                                    alert("Aviso: Não foi possível salvar no banco de dados. A alteração foi salva temporariamente apenas neste computador.");
                                  } else {
                                    alert("Configuração salva com sucesso no banco de dados!");
                                  }
                                  setEditingColab(null);
                                } catch (err) {
                                  alert("Erro ao salvar: " + err.message);
                                }
                              }}
                              className="px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-white font-extrabold rounded-lg shadow-sm"
                            >
                              Salvar Configuração
                            </button>
                          </div>
                        </div>
                      ) : null}

                      <div className="flex-1 overflow-x-auto">
                        <table className="w-full text-left text-xs divide-y divide-slate-100">
                          <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-500">
                            <tr>
                              <th className="px-4 py-3">Colaborador</th>
                              <th className="px-4 py-3">Status de Equipe</th>
                              <th className="px-4 py-3 text-center">Data Admissão</th>
                              <th className="px-4 py-3 text-right">Bônus Base (R$)</th>
                              <th className="px-4 py-3 text-right">Ações</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                            {collaborators.map(c => {
                              const config = colabConfigs.find(cfg => cfg.colaborador === c.id);
                              
                              let statusText = "Não Participa";
                              let statusColor = "text-slate-400";
                              
                              if (config) {
                                if (config.is_lider) {
                                  statusText = "Líder de Equipe";
                                  statusColor = "text-amber-600 font-bold";
                                } else if (config.lider) {
                                  const lUser = collaborators.find(u => u.id === config.lider);
                                  statusText = `Membro (Líder: ${lUser ? lUser.name : "N/D"})`;
                                  statusColor = "text-blue-600";
                                } else {
                                  statusText = "Membro Sem Equipe";
                                  statusColor = "text-yellow-600";
                                }
                              }

                              return (
                                <tr key={c.id} className="hover:bg-slate-50/30">
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span className="font-bold text-slate-800">{c.name}</span>
                                    <p className="text-[9px] text-slate-400 font-mono">@{c.username}</p>
                                  </td>
                                  <td className={`px-4 py-3 ${statusColor}`}>
                                    {statusText}
                                  </td>
                                  <td className="px-4 py-3 text-center font-mono">
                                    {config?.data_admissao ? formatAdmissao(config.data_admissao) : "-"}
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono font-black text-slate-700">
                                    {config ? `R$ ${config.bonus_base.toFixed(2)}` : "-"}
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <button 
                                      onClick={() => {
                                        setEditingColab(c.id);
                                        setEditIsLider(config ? config.is_lider : false);
                                        setEditLider(config ? config.lider : "");
                                        setEditBonusBase(config ? config.bonus_base : 0);
                                        setEditDataAdmissao(config ? (config.data_admissao ? config.data_admissao.substring(0, 10) : "") : "");
                                      }}
                                      className="px-2.5 py-1 text-[10px] bg-slate-100 hover:bg-slate-200/80 rounded font-bold text-slate-650 transition-colors"
                                    >
                                      Configurar
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

          </div>
        )}
      </div>

      {/* ─── DETALHES COLABORADOR MODAL (MASTER VIEWS) ─── */}
      {detailColab && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border border-slate-200 flex flex-col max-h-[80vh] animate-scale-up text-left">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center border text-xs text-slate-500 font-bold overflow-hidden">
                  {detailColab.avatar ? (
                    <img src={pb.files.getURL(detailColab, detailColab.avatar)} className="w-full h-full object-cover" alt="" />
                  ) : (
                    detailColab.name.slice(0,2).toUpperCase()
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-800">
                    Ocorrências de {detailColab.name}
                  </h3>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Filtro: {MONTHS.find(m => m.value === selectedMonth)?.label}/{selectedYear}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setDetailColab(null)} 
                className="text-slate-400 hover:text-slate-650 font-black text-lg p-1.5"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
              {!detailColab.isActive ? (
                <div className="py-12 text-center text-slate-400 flex flex-col justify-center items-center h-full">
                  <Info size={32} className="text-amber-500 mb-2 opacity-80" />
                  <p className="text-xs font-black text-slate-700 uppercase tracking-wider">Não Elegível no Período</p>
                  <p className="text-[10px] text-slate-450 mt-1.5 leading-normal">
                    Admitido em <strong>{formatAdmissao(detailColab.dataAdmissao)}</strong>.
                    <br />
                    A pontuação e o bônus só iniciam no primeiro mês completo trabalhado.
                  </p>
                </div>
              ) : detailColab.occurrences.length === 0 ? (
                <div className="py-12 text-center text-slate-400 flex flex-col justify-center items-center h-full">
                  <CheckCircle2 size={36} className="text-emerald-500 mb-2 opacity-60" />
                  <p className="text-xs font-bold">Excelente! Nenhuma ocorrência neste mês.</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">O saldo de bônus do colaborador permanece integral.</p>
                </div>
              ) : (
                <table className="w-full text-left text-xs divide-y divide-slate-100">
                  <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-500">
                    <tr>
                      <th className="px-4 py-2.5">Data</th>
                      <th className="px-4 py-2.5">Ocorrência</th>
                      <th className="px-4 py-2.5 text-center">Débito</th>
                      <th className="px-4 py-2.5">Responsável</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                    {detailColab.occurrences.map(occ => (
                      <tr key={occ.id} className="hover:bg-slate-50/50">
                        <td className="px-4 py-3 whitespace-nowrap font-mono">{occ.data}</td>
                        <td className="px-4 py-3">
                          {occ.colaborador !== detailColab.id && occ.expand?.colaborador?.name && (
                            <span className="bg-blue-50 border border-blue-100 text-blue-700 font-bold px-1.5 py-0.5 rounded text-[9px] mr-1.5 uppercase">
                              {occ.expand.colaborador.name.split(" ")[0]}
                            </span>
                          )}
                          {occ.ocorrencia}
                        </td>
                        <td className="px-4 py-3 text-center text-red-650 font-black">-{occ.pontos_debitados} pt</td>
                        <td className="px-4 py-3 font-mono text-[10px] text-slate-500">
                          {occ.expand?.responsavel?.name || "SISTEMA"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="p-4 border-t border-slate-100 bg-slate-50 shrink-0 flex justify-between items-center text-xs font-bold">
              {!detailColab.isActive ? (
                <div className="text-slate-450 italic font-bold">Colaborador não elegível no período</div>
              ) : (
                <>
                  <div className="flex flex-col gap-0.5 text-left">
                    <div>
                      Saldo final no mês: <span className="font-black text-slate-800">{detailColab.remainingPoints} / {detailColab.maxPoints} pts</span>
                    </div>
                    <div className="text-[10px] text-slate-500 font-medium">
                      Presença: {detailColab.isLider ? "Isento (Líder)" : `${detailColab.totalTrabalhados} / ${detailColab.totalUteis} d (Faltas: ${detailColab.totalFaltas} | Atestados: ${detailColab.totalAtestados} | Férias: ${detailColab.totalFerias})`}
                    </div>
                  </div>
                  <div>
                    Bônus ativo: <span className="font-black text-amber-600">R$ {detailColab.expectedBonus.toFixed(2)} ({detailColab.activeBonusPct}%)</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
